import { useState, useEffect, useCallback } from 'react';
import { LockOutlined } from '@ant-design/icons';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import ThreadPanelHeader from './ThreadPanelHeader';
import ActivityBar from './ActivityBar';
import { UserAvatar } from './UserAvatar';
import { useChannelStore, useMessageStore } from '../stores';
import { useTaskStore } from '../stores/useTaskStore';
import { dispatchOutgoingMessage } from '../stores/actions';
import { pendingThreadDraftKey, threadDraftKey } from '../hooks/useComposerDraft';
import * as api from '../lib/api';
import { routes } from '../lib/routes';
import type { Task } from '../lib/api';
import type { Message } from '../types';

const EMPTY_MESSAGES: Message[] = [];

type Props =
  | { threadId: string; onClose: () => void; pendingMessageId?: undefined; channelId?: undefined; onThreadCreated?: undefined }
  | { threadId?: undefined; pendingMessageId: string; channelId: string; onClose: () => void; onThreadCreated: (threadId: string) => void };

export default function ThreadPanel(props: Props) {
  if (props.threadId) {
    return <ExistingThreadPanel threadId={props.threadId} onClose={props.onClose} />;
  }
  return (
    <PendingThreadPanel
      messageId={props.pendingMessageId!}
      channelId={props.channelId!}
      onClose={props.onClose}
      onThreadCreated={props.onThreadCreated!}
    />
  );
}

function ExistingThreadPanel({ threadId, onClose }: { threadId: string; onClose: () => void }) {
  const loadMessages = useMessageStore(s => s.loadMessages);
  const [resolvedTask, setResolvedTask] = useState<Task | null>(null);
  const upsertTask = useTaskStore(s => s.upsertTask);
  const task = useTaskStore(s => resolvedTask
    ? s.byTaskId[resolvedTask.id] ?? resolvedTask
    : undefined);
  const [threadName, setThreadName] = useState('Thread');
  const loadChannels = useChannelStore(s => s.loadChannels);
  const parentChannel = useChannelStore(s => task
    ? s.channels.find(channel => channel.id === task.channel_id)
    : undefined);
  const isReadOnlyPublicThread = !!task && parentChannel?.visibility === 'public' && parentChannel.is_member === false;
  const [joining, setJoining] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setResolvedTask(null);
    void api.getChannel(threadId).then(({ channel: thread }) => {
      if (!cancelled) setThreadName(thread.name || 'Thread');
    }).catch(() => {});
    void api.getTaskByThread(threadId).then((resolved) => {
      if (cancelled) return;
      setResolvedTask(resolved);
      upsertTask(resolved);
    }).catch(() => {
      // A non-task Thread or an inaccessible Task stays task-free. The server
      // makes both cases non-disclosing through the same lookup contract.
    });
    return () => { cancelled = true; };
  }, [threadId, upsertTask]);


  useEffect(() => {
    loadMessages(threadId);
  }, [threadId, loadMessages]);

  const handleJoin = useCallback(async () => {
    if (!task || joining) return;
    setJoining(true);
    try {
      await api.joinChannel(task.channel_id);
      await loadChannels();
    } finally {
      setJoining(false);
    }
  }, [joining, loadChannels, task]);

  return (
    <div className="thread-panel">
      <ThreadPanelHeader
        title={threadName}
        onClose={onClose}
        task={task}
        assigneeName={task?.assignee?.display_name}
        parentChannelRoute={task?.thread_id
          ? `${routes.thread(task.guild_id, task.channel_id, task.thread_id)}?tab=chat`
          : undefined}
        conversationId={threadId}
      />
      <div className="thread-panel-messages">
        <MessageList channelId={threadId} />
      </div>
      <ActivityBar channelId={threadId} />
      <div className="thread-panel-input">
        {isReadOnlyPublicThread ? (
          <div className="readonly-composer-placeholder" role="status">
            <LockOutlined aria-hidden="true" />
            <span>Read-only</span>
            <button onClick={handleJoin} disabled={joining}>
              {joining ? 'Joining...' : `Join #${parentChannel?.name ?? 'channel'} to reply`}
            </button>
          </div>
        ) : (
          <MessageInput channelId={threadId} draftKey={threadDraftKey(threadId)} />
        )}
      </div>
    </div>
  );
}

function PendingThreadPanel({ messageId, channelId, onClose, onThreadCreated }: {
  messageId: string;
  channelId: string;
  onClose: () => void;
  onThreadCreated: (threadId: string) => void;
}) {
  const messages = useMessageStore(s => s.messages[channelId]) ?? EMPTY_MESSAGES;

  const originMessage = messages.find(m => m.id === messageId);
  const senderName = originMessage?.sender_name ?? 'Unknown';

  // The thread is only persisted by this first reply, so a failed creation has
  // to reject rather than be swallowed: the composer keeps the typed text and
  // its stored draft, and the member can send again. Once the thread exists the
  // reply is an ordinary message in an ordinary channel, so it goes out on the
  // same optimistic rail as every reply after it — a pending row in the thread
  // that turns into a failed one the member can retry if no ack arrives, rather
  // than a frame handed to a socket that may not be open.
  const handleSend = useCallback(async (content: string, mentions: string[]) => {
    const threadName = (originMessage?.content || 'Thread').slice(0, 30);
    const thread = await api.createThreadFromMessage(channelId, messageId, threadName);
    dispatchOutgoingMessage({ channelId: thread.id, content, contentType: 'text', mentions });
    onThreadCreated(thread.id);
  }, [originMessage, channelId, messageId, onThreadCreated]);

  return (
    <div className="thread-panel">
      <ThreadPanelHeader title="New Thread" onClose={onClose} />
      <div className="thread-panel-messages thread-panel-pending">
        {originMessage && (
          <div className="thread-origin-message">
            <div className="message-item">
              <UserAvatar userId={originMessage.sender_id} displayName={senderName} size={36} />
              <div className="message-body">
                <div className="message-header">
                  <span className="message-sender">{senderName}</span>
                </div>
                <div className="message-text">{originMessage.content}</div>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="thread-panel-input">
        <MessageInput
          channelId={channelId}
          draftKey={pendingThreadDraftKey(messageId)}
          onSendOverride={handleSend}
        />
      </div>
    </div>
  );
}
